import { useState, useEffect, useMemo } from 'react';
import { Navbar } from '@/components/Navbar';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Users, Package, AlertTriangle, CheckCircle, MapPin, DollarSign, XCircle, Check, Search, Filter, Calendar, BarChart3, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Full status workflow
const statusOptions = [
  'Submitted',
  'Quoted',
  'Dispatched',
  'Picked Up',
  'Delivered',
  'Returned',
  'Completed',
  // Legacy statuses for backward compatibility
  'Submitted – Pending DLT Approval',
  'Pending Approval',
  'Rate Requested',
  'Approved',
  'Rejected'
];

// Status color mapping
const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower.includes('submitted')) return 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20';
  if (statusLower.includes('quoted')) return 'bg-orange-400/10 text-orange-400 border-orange-400/20';
  if (statusLower.includes('dispatched')) return 'bg-blue-400/10 text-blue-400 border-blue-400/20';
  if (statusLower.includes('picked')) return 'bg-indigo-400/10 text-indigo-400 border-indigo-400/20';
  if (statusLower.includes('delivered')) return 'bg-purple-400/10 text-purple-400 border-purple-400/20';
  if (statusLower.includes('returned')) return 'bg-teal-400/10 text-teal-400 border-teal-400/20';
  if (statusLower.includes('completed')) return 'bg-green-400/10 text-green-400 border-green-400/20';
  if (statusLower.includes('rejected')) return 'bg-red-400/10 text-red-400 border-red-400/20';
  return 'bg-slate-400/10 text-slate-400 border-slate-400/20';
};

export default function AdminPanel({ user }) {
  const [loads, setLoads] = useState([]);
  const [users, setUsers] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [updateData, setUpdateData] = useState({ 
    status: '', 
    internal_notes: '',
    linehaul_override: '',
    hazmat_charge: '',
    overweight_charge: '',
    chassis_charge: '',
    total_override: '',
    rejection_reason: ''
  });
  
  // Search and Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
    fetchVisitorStats();
  }, []);

  const fetchVisitorStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/visitor-stats?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVisitorStats(res.data);
    } catch (error) {
      console.log('Visitor stats not available');
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [loadsRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/loads`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setLoads(loadsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Filtered loads based on search and filters
  const filteredLoads = useMemo(() => {
    return loads.filter(load => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        load.load_number?.toLowerCase().includes(searchLower) ||
        load.user_name?.toLowerCase().includes(searchLower) ||
        load.user_email?.toLowerCase().includes(searchLower) ||
        load.commodity?.toLowerCase().includes(searchLower) ||
        load.container_number?.toLowerCase().includes(searchLower);
      
      // Status filter
      const matchesStatus = filterStatus === 'all' || load.status === filterStatus;
      
      // Type filter
      const matchesType = filterType === 'all' || 
        load.load_type?.toLowerCase() === filterType.toLowerCase();
      
      // Date filter
      let matchesDate = true;
      if (filterDateFrom) {
        const loadDate = new Date(load.created_at);
        const fromDate = new Date(filterDateFrom);
        matchesDate = matchesDate && loadDate >= fromDate;
      }
      if (filterDateTo) {
        const loadDate = new Date(load.created_at);
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59);
        matchesDate = matchesDate && loadDate <= toDate;
      }
      
      return matchesSearch && matchesStatus && matchesType && matchesDate;
    });
  }, [loads, searchTerm, filterStatus, filterType, filterDateFrom, filterDateTo]);

  // Dashboard statistics
  const stats = useMemo(() => {
    const pending = loads.filter(l => 
      l.status?.includes('Pending') || l.status?.includes('Submitted') || l.status === 'Rate Requested'
    ).length;
    const approved = loads.filter(l => l.status === 'Approved').length;
    const completed = loads.filter(l => l.status === 'Completed' || l.status === 'Delivered').length;
    const totalRevenue = loads.reduce((sum, l) => sum + (l.total_override || l.calculated_total || 0), 0);
    
    return { pending, approved, completed, totalRevenue, total: loads.length };
  }, [loads]);

  const handleUpdateLoad = async (loadId) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        status: updateData.status,
        internal_notes: updateData.internal_notes
      };
      
      // Add overrides if provided
      if (updateData.linehaul_override) payload.linehaul_override = parseInt(updateData.linehaul_override);
      if (updateData.hazmat_charge) payload.hazmat_charge = parseInt(updateData.hazmat_charge);
      if (updateData.overweight_charge) payload.overweight_charge = parseInt(updateData.overweight_charge);
      if (updateData.chassis_charge) payload.chassis_charge = parseInt(updateData.chassis_charge);
      if (updateData.total_override) payload.total_override = parseInt(updateData.total_override);
      if (updateData.rejection_reason) payload.rejection_reason = updateData.rejection_reason;
      
      await axios.patch(`${BACKEND_URL}/api/admin/loads/${loadId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Load updated successfully');
      fetchData();
      setSelectedLoad(null);
      resetUpdateData();
    } catch (error) {
      toast.error('Failed to update load');
    }
  };

  const quickApprove = async (loadId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${BACKEND_URL}/api/admin/loads/${loadId}`, {
        status: 'Approved'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Load approved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve load');
    }
  };

  const quickReject = async (loadId, reason = 'Rejected by admin') => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${BACKEND_URL}/api/admin/loads/${loadId}`, {
        status: 'Rejected',
        rejection_reason: reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Load rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject load');
    }
  };

  const resetUpdateData = () => {
    setUpdateData({ 
      status: '', 
      internal_notes: '',
      linehaul_override: '',
      hazmat_charge: '',
      overweight_charge: '',
      chassis_charge: '',
      total_override: '',
      rejection_reason: ''
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterType('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${BACKEND_URL}/api/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const isPendingApproval = (status) => {
    return status === 'Submitted – Pending DLT Approval' || 
           status === 'Pending Approval' || 
           status === 'Rate Requested';
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar user={user} />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="admin-panel-page">
        <h1 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase mb-8">
          <span className="text-[#FFA726]">ADMIN</span>
          <span className="text-white"> PANEL</span>
        </h1>
        
        {/* Dashboard Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="text-[#00A6E8]" size={20} />
              <span className="text-slate-400 text-sm">Total Loads</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="text-yellow-400" size={20} />
              <span className="text-slate-400 text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
          </div>
          
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="text-green-400" size={20} />
              <span className="text-slate-400 text-sm">Approved</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
          </div>
          
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="text-blue-400" size={20} />
              <span className="text-slate-400 text-sm">Completed</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{stats.completed}</p>
          </div>
          
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="text-green-400" size={20} />
              <span className="text-slate-400 text-sm">Est. Revenue</span>
            </div>
            <p className="text-2xl font-bold text-green-400">${stats.totalRevenue.toLocaleString()}</p>
          </div>
        </div>
        
        {/* Visitor Tracking Widget */}
        {visitorStats && (
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-4 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <BarChart3 className="text-[#00A6E8]" size={20} />
                Website Traffic (Last 30 Days)
              </h3>
              <span className="text-slate-400 text-sm">{visitorStats.period_days} day period</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#1E293B]/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Today</p>
                <p className="text-2xl font-bold text-[#00A6E8]">{visitorStats.today_visits}</p>
              </div>
              <div className="bg-[#1E293B]/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Total Visits</p>
                <p className="text-2xl font-bold text-white">{visitorStats.total_visits}</p>
              </div>
              <div className="bg-[#1E293B]/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Daily Average</p>
                <p className="text-2xl font-bold text-green-400">{visitorStats.average_daily}</p>
              </div>
              <div className="bg-[#1E293B]/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Top Page</p>
                <p className="text-lg font-bold text-[#FFA726]">
                  {visitorStats.page_breakdown?.[0]?.page || 'N/A'}
                </p>
              </div>
            </div>
            {visitorStats.page_breakdown?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Page Visits</p>
                <div className="flex flex-wrap gap-2">
                  {visitorStats.page_breakdown.slice(0, 5).map((page, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#00A6E8]/10 text-[#00A6E8] text-sm rounded-full">
                      {page.page}: {page.visits}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        <Tabs defaultValue="loads" className="w-full">
          <TabsList className="bg-[#0F172A] border border-white/10">
            <TabsTrigger value="loads" className="data-[state=active]:bg-[#00A6E8] data-[state=active]:text-[#0A0E1A]" data-testid="loads-tab">
              Loads Management
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#00A6E8] data-[state=active]:text-[#0A0E1A]" data-testid="users-tab">
              Users Management
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="loads" className="mt-6" data-testid="loads-content">
            {/* Search and Filter Bar */}
            <div className="bg-[#0F172A] border border-white/10 rounded-lg p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      placeholder="Search by Load #, Customer, Email, Container..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-[#1E293B] border-white/10 text-white"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                
                {/* Quick Filters */}
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px] bg-[#1E293B] border-white/10 text-white" data-testid="status-filter">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-[300px]">
                    <SelectItem value="all">All Statuses</SelectItem>
                    {statusOptions.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[150px] bg-[#1E293B] border-white/10 text-white" data-testid="type-filter">
                    <SelectValue placeholder="Filter by Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="import">Import</SelectItem>
                    <SelectItem value="export">Export</SelectItem>
                    <SelectItem value="rate request">Rate Request</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="border-white/10 text-slate-300"
                >
                  <Filter size={16} className="mr-1" />
                  {showFilters ? 'Hide' : 'More'} Filters
                </Button>
                
                {(searchTerm || filterStatus !== 'all' || filterType !== 'all' || filterDateFrom || filterDateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-red-400 hover:text-red-300"
                  >
                    Clear All
                  </Button>
                )}
              </div>
              
              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs mb-1 block">Date From</Label>
                    <Input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="bg-[#1E293B] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs mb-1 block">Date To</Label>
                    <Input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="bg-[#1E293B] border-white/10 text-white"
                    />
                  </div>
                </div>
              )}
              
              {/* Results count */}
              <div className="mt-3 text-sm text-slate-400">
                Showing {filteredLoads.length} of {loads.length} loads
              </div>
            </div>
            
            {loading ? (
              <p className="text-slate-400">Loading loads...</p>
            ) : filteredLoads.length === 0 ? (
              <div className="bg-[#0F172A] border border-white/10 rounded-lg p-8 text-center">
                <Package className="mx-auto text-slate-500 mb-4" size={48} />
                <p className="text-slate-400">
                  {loads.length === 0 ? 'No loads submitted yet.' : 'No loads match your filters.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLoads.map((load) => (
                  <div key={load.id} className="bg-[#0F172A] border border-white/10 rounded-lg p-4 md:p-6" data-testid={`load-card-${load.load_number}`}>
                    {/* Header */}
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">{load.load_number}</h3>
                        <p className="text-slate-400 text-sm">{load.load_type?.toUpperCase()} • {load.user_name} ({load.user_email})</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase border ${getStatusColor(load.status)}`}>
                          {load.status}
                        </span>
                        
                        {/* Quick Actions for Pending Loads */}
                        {isPendingApproval(load.status) && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-green-500 text-white hover:bg-green-600"
                              onClick={() => quickApprove(load.id)}
                              data-testid={`approve-btn-${load.load_number}`}
                            >
                              <Check size={14} className="mr-1" /> Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-400 text-red-400 hover:bg-red-400/10"
                              onClick={() => quickReject(load.id)}
                              data-testid={`reject-btn-${load.load_number}`}
                            >
                              <XCircle size={14} className="mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        
                        {/* Edit Dialog */}
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="border-[#00A6E8] text-[#00A6E8]"
                              onClick={() => {
                                setSelectedLoad(load);
                                setUpdateData({ 
                                  status: load.status, 
                                  internal_notes: load.internal_notes || '',
                                  linehaul_override: load.linehaul_override || '',
                                  hazmat_charge: load.hazmat_charge || '',
                                  overweight_charge: load.overweight_charge || '',
                                  chassis_charge: load.chassis_charge || '',
                                  total_override: load.total_override || '',
                                  rejection_reason: load.rejection_reason || ''
                                });
                              }}
                              data-testid={`edit-load-btn-${load.load_number}`}
                            >
                              <Edit size={16} className="mr-1" /> Edit
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-white">Update Load: {load.load_number}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              {/* Status */}
                              <div>
                                <Label className="text-slate-300">Status</Label>
                                <Select value={updateData.status} onValueChange={(value) => setUpdateData({...updateData, status: value})}>
                                  <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white" data-testid="status-select">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-[300px]">
                                    {statusOptions.map(status => (
                                      <SelectItem key={status} value={status}>{status}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Zone C: Convert to Approved Quote */}
                              {load.zone_classification === 'C' && load.status === 'Rate Requested' && (
                                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                                  <Label className="text-yellow-400 font-semibold">Zone C - Custom Quote Required</Label>
                                  <p className="text-slate-400 text-xs mb-3">
                                    Enter custom Line Haul and approve to convert to confirmed quote.
                                  </p>
                                </div>
                              )}
                              
                              {/* Line Haul Override */}
                              <div className="bg-[#FFA726]/10 border border-[#FFA726]/20 rounded-lg p-4">
                                <Label className="text-[#FFA726] font-semibold">Modify Line Haul</Label>
                                <p className="text-slate-400 text-xs mb-2">
                                  Calculated: ${load.calculated_linehaul || 'N/A'} | Current Override: ${load.linehaul_override || 'None'}
                                </p>
                                <Input
                                  type="number"
                                  value={updateData.linehaul_override}
                                  onChange={(e) => setUpdateData({...updateData, linehaul_override: e.target.value})}
                                  placeholder="Enter custom Line Haul"
                                  className="bg-[#1E293B] border-white/10 text-white"
                                  data-testid="linehaul-override-input"
                                />
                              </div>
                              
                              {/* Modify Accessorials */}
                              <div className="bg-[#00A6E8]/10 border border-[#00A6E8]/20 rounded-lg p-4">
                                <Label className="text-[#00A6E8] font-semibold mb-3 block">Modify Accessorials</Label>
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <Label className="text-slate-400 text-xs">HAZMAT</Label>
                                    <Input
                                      type="number"
                                      value={updateData.hazmat_charge}
                                      onChange={(e) => setUpdateData({...updateData, hazmat_charge: e.target.value})}
                                      placeholder={load.hazmat_charge || '0'}
                                      className="bg-[#1E293B] border-white/10 text-white text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-slate-400 text-xs">Overweight</Label>
                                    <Input
                                      type="number"
                                      value={updateData.overweight_charge}
                                      onChange={(e) => setUpdateData({...updateData, overweight_charge: e.target.value})}
                                      placeholder={load.overweight_charge || '0'}
                                      className="bg-[#1E293B] border-white/10 text-white text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-slate-400 text-xs">Chassis</Label>
                                    <Input
                                      type="number"
                                      value={updateData.chassis_charge}
                                      onChange={(e) => setUpdateData({...updateData, chassis_charge: e.target.value})}
                                      placeholder={load.chassis_charge || '0'}
                                      className="bg-[#1E293B] border-white/10 text-white text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                              
                              {/* Override Total */}
                              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                                <Label className="text-green-400 font-semibold">Override Total</Label>
                                <p className="text-slate-400 text-xs mb-2">
                                  Calculated Total: ${load.calculated_total || 'N/A'}
                                </p>
                                <Input
                                  type="number"
                                  value={updateData.total_override}
                                  onChange={(e) => setUpdateData({...updateData, total_override: e.target.value})}
                                  placeholder="Enter final total override"
                                  className="bg-[#1E293B] border-white/10 text-white"
                                  data-testid="total-override-input"
                                />
                              </div>
                              
                              {/* Rejection Reason */}
                              {updateData.status === 'Rejected' && (
                                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                                  <Label className="text-red-400 font-semibold">Rejection Reason</Label>
                                  <Textarea
                                    value={updateData.rejection_reason}
                                    onChange={(e) => setUpdateData({...updateData, rejection_reason: e.target.value})}
                                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                                    rows={2}
                                    placeholder="Reason for rejection..."
                                  />
                                </div>
                              )}
                              
                              {/* Internal Notes */}
                              <div>
                                <Label className="text-slate-300">Internal Notes</Label>
                                <Textarea
                                  value={updateData.internal_notes}
                                  onChange={(e) => setUpdateData({...updateData, internal_notes: e.target.value})}
                                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                                  rows={2}
                                  data-testid="internal-notes-input"
                                />
                              </div>
                              
                              <Button 
                                onClick={() => handleUpdateLoad(load.id)} 
                                className="w-full bg-[#00A6E8] text-[#0A0E1A]"
                                data-testid="save-load-btn"
                              >
                                Save Changes
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                    
                    {/* Container Count & Flags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-[#00A6E8]/10 text-[#00A6E8] text-xs rounded-full font-semibold flex items-center gap-1">
                        <Package size={12} />
                        {load.number_of_containers || load.containers?.length || 1} Container{(load.number_of_containers || load.containers?.length || 1) > 1 ? 's' : ''}
                      </span>
                      
                      {load.has_hazmat && (
                        <span className="px-3 py-1 bg-yellow-400/10 text-yellow-400 text-xs rounded-full font-semibold flex items-center gap-1">
                          <AlertTriangle size={12} />
                          HAZMAT
                        </span>
                      )}
                      
                      {load.has_overweight && (
                        <span className="px-3 py-1 bg-red-400/10 text-red-400 text-xs rounded-full font-semibold flex items-center gap-1">
                          <AlertTriangle size={12} />
                          Overweight
                        </span>
                      )}
                      
                      {load.chassis_charge > 0 && (
                        <span className="px-3 py-1 bg-purple-400/10 text-purple-400 text-xs rounded-full font-semibold flex items-center gap-1">
                          <CheckCircle size={12} />
                          Chassis
                        </span>
                      )}
                      
                      {load.rejection_reason && (
                        <span className="px-3 py-1 bg-red-400/10 text-red-400 text-xs rounded-full font-semibold">
                          Rejected: {load.rejection_reason}
                        </span>
                      )}
                    </div>
                    
                    {/* Container Numbers Display */}
                    <div className="mb-4 p-3 bg-[#0A0E1A] rounded-lg">
                      <p className="text-slate-500 text-xs mb-2">Container Number(s)</p>
                      <div className="flex flex-wrap gap-2">
                        {load.containers && load.containers.length > 0 ? (
                          load.containers.map((c, idx) => (
                            <span key={idx} className="px-3 py-1 bg-[#00A6E8]/20 text-[#00A6E8] text-sm font-bold rounded border border-[#00A6E8]/30">
                              {c.container_number || `Container ${idx + 1}`}
                            </span>
                          ))
                        ) : load.container_number ? (
                          <span className="px-3 py-1 bg-[#00A6E8]/20 text-[#00A6E8] text-sm font-bold rounded border border-[#00A6E8]/30">
                            {load.container_number}
                          </span>
                        ) : (
                          <span className="text-slate-500 text-sm">No container numbers</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Main Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div>
                        <p className="text-slate-500 text-xs">Container Size</p>
                        <p className="text-white font-semibold">{load.container_size || load.containers?.[0]?.container_size || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Commodity</p>
                        <p className="text-white font-semibold">{load.commodity || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">SSL</p>
                        <p className="text-white font-semibold">{load.steamship_line || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Created</p>
                        <p className="text-white font-semibold">{new Date(load.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    
                    {/* Address Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                      {load.delivery_address && (
                        <div className="p-3 bg-[#1E293B]/30 rounded-lg">
                          <p className="text-slate-500 text-xs mb-1">Delivery Address</p>
                          <p className="text-white font-semibold">
                            {typeof load.delivery_address === 'object' 
                              ? `${load.delivery_address.street || ''}, ${load.delivery_address.city || ''}, ${load.delivery_address.state || ''} ${load.delivery_address.zip || ''}`
                              : load.delivery_address}
                          </p>
                        </div>
                      )}
                      {load.loading_address && (
                        <div className="p-3 bg-[#1E293B]/30 rounded-lg">
                          <p className="text-slate-500 text-xs mb-1">Loading Address</p>
                          <p className="text-white font-semibold">
                            {typeof load.loading_address === 'object'
                              ? `${load.loading_address.street || ''}, ${load.loading_address.city || ''}, ${load.loading_address.state || ''} ${load.loading_address.zip || ''}`
                              : load.loading_address}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Rate Information */}
                    <div className="bg-[#1E293B]/50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <MapPin className="text-[#00A6E8]" size={16} />
                        <div>
                          <p className="text-slate-500 text-xs">Distance</p>
                          <p className="text-white font-semibold">
                            {load.driving_distance_miles ? `${load.driving_distance_miles} mi` : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-slate-500 text-xs">Zone</p>
                        <p className={`font-semibold ${
                          load.zone_classification === 'C' ? 'text-yellow-400' : 'text-green-400'
                        }`}>
                          {load.zone_classification || 'N/A'}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-slate-500 text-xs">Est. Line Haul</p>
                        <p className="text-white font-semibold">
                          {load.calculated_linehaul ? `$${load.calculated_linehaul}` : 'Pending'}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-slate-500 text-xs">LH Override</p>
                        <p className="text-[#FFA726] font-semibold">
                          {load.linehaul_override ? `$${load.linehaul_override}` : '-'}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <DollarSign className="text-green-400" size={16} />
                        <div>
                          <p className="text-slate-500 text-xs">{load.total_override ? 'Final Total' : 'Est. Total'}</p>
                          <p className="text-green-400 font-bold">
                            {load.total_override ? `$${load.total_override}` : (load.calculated_total ? `$${load.calculated_total}` : 'Pending')}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Accessorial Breakdown */}
                    {(load.hazmat_charge || load.overweight_charge || load.chassis_charge) && (
                      <div className="mt-4 bg-[#0F172A] border border-white/5 rounded-lg p-3">
                        <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider">Accessorial Breakdown</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          {load.hazmat_charge > 0 && (
                            <span className="text-yellow-400">HAZMAT: ${load.hazmat_charge}</span>
                          )}
                          {load.overweight_charge > 0 && (
                            <span className="text-red-400">Overweight: ${load.overweight_charge}</span>
                          )}
                          {load.chassis_charge > 0 && (
                            <span className="text-purple-400">Chassis: ${load.chassis_charge}</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Change Log Timeline (Admin Only) */}
                    {load.change_log && load.change_log.length > 0 && (
                      <div className="mt-4 bg-[#0F172A] border border-white/5 rounded-lg p-4">
                        <p className="text-slate-400 text-xs mb-3 uppercase tracking-wider flex items-center gap-2">
                          <Clock size={12} />
                          Change Log Timeline
                        </p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {[...load.change_log].reverse().map((entry, idx) => (
                            <div key={idx} className="flex gap-3 text-xs border-l-2 border-[#00A6E8]/30 pl-3 py-1">
                              <div className="flex-shrink-0 w-24 text-slate-500">
                                {new Date(entry.timestamp).toLocaleDateString()} {new Date(entry.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                              <div className="flex-1">
                                <span className="text-[#00A6E8] font-semibold">{entry.action}</span>
                                <span className="text-slate-400 ml-2">{entry.details}</span>
                              </div>
                              <div className="text-slate-600 text-[10px]">{entry.user.split('@')[0]}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="users" className="mt-6" data-testid="users-content">
            {loading ? (
              <p className="text-slate-400">Loading users...</p>
            ) : users.length === 0 ? (
              <div className="bg-[#0F172A] border border-white/10 rounded-lg p-8 text-center">
                <Users className="mx-auto text-slate-500 mb-4" size={48} />
                <p className="text-slate-400">No users registered yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((u) => (
                  <div key={u.id} className="bg-[#0F172A] border border-white/10 rounded-lg p-4 md:p-6" data-testid={`user-card-${u.email}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">{u.company_name || u.individual_name}</h3>
                        <p className="text-slate-400 text-sm">{u.email} • {u.client_type}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${
                          u.verified 
                            ? 'bg-green-400/10 text-green-400 border border-green-400/20' 
                            : 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/20'
                        }`}>
                          {u.verified ? 'Verified' : 'Pending'}
                        </span>
                        {!u.verified && (
                          <Button 
                            size="sm" 
                            className="bg-[#00A6E8] text-[#0A0E1A]"
                            onClick={() => handleUpdateUser(u.id, { verified: true })}
                            data-testid={`verify-user-btn-${u.email}`}
                          >
                            Verify
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500 text-xs">Phone</p>
                        <p className="text-white font-semibold">{u.phone}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Country</p>
                        <p className="text-white font-semibold">{u.country}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Payment Terms</p>
                        <p className="text-white font-semibold">{u.preferred_payment_terms}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Rate Agreement</p>
                        <p className="text-white font-semibold">{u.has_rate_agreement ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
